/**
 * 🎁 LootHub — Daily Mystery Box + 🐦 Roaming Phoenix.
 *
 * One mounted-once overlay at the app root. Polls /api/loot/state every 60s
 * so users always know when a phoenix is flying and when their daily box is
 * ready. Click → claim → animated reveal modal.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { playSfx } from "@/utils/battleSfx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

export default function LootHub() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [reveal, setReveal] = useState(null); // { reward, source }
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/loot/state`);
      setState(r.data);
    } catch (e) {
      // silent — non-critical
    }
  };

  useEffect(() => {
    if (!user) {
      setState(null);
      return;
    }
    refresh();
    pollRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const claimBox = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/loot/box/claim`);
      setReveal({ reward: r.data.reward, source: "🎁 Daily Mystery Box" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open box.");
    }
    setBusy(false);
  };

  const claimPhoenix = async () => {
    if (busy || !state?.phoenix?.claim_token) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/loot/phoenix/claim`, {
        claim_token: state.phoenix.claim_token,
      });
      setReveal({ reward: r.data.reward, source: "🐦 Roaming Phoenix" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The phoenix escaped.");
      refresh();
    }
    setBusy(false);
  };

  // 📱 Mystery Box claim is now triggered from the consolidated BottomTabBar.
  // Listen for the global event so the reveal modal still fires from here.
  // (Must be above any early-return to satisfy react-hooks/rules-of-hooks.)
  useEffect(() => {
    if (!user) return undefined;
    const onTrigger = () => { claimBox(); };
    window.addEventListener("omni:claim-mystery-box", onTrigger);
    return () => window.removeEventListener("omni:claim-mystery-box", onTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, busy]);

  if (!user || !state) return null;

  return (
    <>
      {/* 🐦 Roaming Phoenix — full-width fly-across animation when it's airborne */}
      {state.phoenix?.flying && (
        <RoamingPhoenix
          expiresIn={state.phoenix.expires_in_seconds}
          onClick={claimPhoenix}
          busy={busy}
        />
      )}

      {/* 🎉 Reveal modal */}
      {reveal && (
        <RewardRevealModal
          reward={reveal.reward}
          source={reveal.source}
          onClose={() => setReveal(null)}
        />
      )}
    </>
  );
}

// ─── 🐦 Roaming Phoenix overlay ──────────────────────────────────────
function RoamingPhoenix({ expiresIn, onClick, busy }) {
  const [secs, setSecs] = useState(expiresIn);
  useEffect(() => { setSecs(expiresIn); }, [expiresIn]);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="fixed inset-x-0 top-0 z-[55] pointer-events-none" style={{ height: 160 }} data-testid="roaming-phoenix-overlay">
      {/* Stationary clickable strip — phoenix sprite flies inside it. */}
      <button
        onClick={onClick}
        disabled={busy}
        data-testid="roaming-phoenix-btn"
        className="absolute inset-x-0 top-4 h-24 pointer-events-auto cursor-pointer disabled:cursor-wait bg-transparent"
        title={`A wild Phoenix appeared! Click anywhere across the top to catch — ${secs}s left`}
      >
        <span
          aria-hidden
          className="absolute roaming-phoenix-fly text-7xl select-none drop-shadow-[0_0_24px_#F97316]"
          style={{ top: 10 }}
        >
          🐦‍🔥
        </span>
      </button>
      <div
        className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-none bg-black/80 border border-[#F97316] text-[#F97316] text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1"
        data-testid="roaming-phoenix-timer"
      >
        🐦 Wild Phoenix · {secs}s · Click anywhere up here!
      </div>
    </div>
  );
}

// ─── 🎉 Reward reveal modal ──────────────────────────────────────────
function RewardRevealModal({ reward, source, onClose }) {
  const amount = reward.amount || reward.count || 1;
  const isJackpot = reward.kind === "kiss" || reward.kind === "egg" || reward.kind === "od";
  useEffect(() => {
    // 🔊 Loot drop sting — slightly longer for jackpots
    playSfx(isJackpot ? "victory" : "loot_drop");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="loot-reveal-modal">
      <div className={`bg-[#0A0A0A] border-2 ${isJackpot ? "border-[#FCD34D]" : "border-[#7C3AED]"} max-w-sm w-full p-6 text-center relative ${isJackpot ? "loot-jackpot-glow" : ""}`}>
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">{source}</div>
        <div className="text-7xl my-4 loot-pop">{reward.icon}</div>
        <div className="font-display text-2xl mb-1 text-white">
          {isJackpot ? "🎊 " : ""}+{amount} {reward.label}
        </div>
        {isJackpot && (
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FCD34D] font-bold mb-2 animate-pulse">
            ⚡ Lucky drop ⚡
          </div>
        )}
        <button
          onClick={onClose}
          data-testid="loot-reveal-close"
          className="mt-4 w-full bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest py-3"
        >
          Claim it
        </button>
      </div>
    </div>
  );
}
